import { FC, useContext, useEffect } from "react";
import { Controller } from "react-hook-form";
import { Form, FormField, Input, ModalDialog, Numeric } from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import { InsertWasherBody, UpdateWasherBody, Washer } from "../../../../http/screwConnector/types/washers";
import useScrewConnectorWashersApi from "../../../../http/screwConnector/useAdminWashers";

const MIN_MEASUREMENT_MM = 0.001;

interface WasherFormBody {
  code: string | null;
  innerDiameter: number | null;
  outerDiameter: number | null;
  height: number | null;
}

interface WasherFormProps {
  washer?: Washer;
  onClose: () => void;
}

const WasherForm: FC<WasherFormProps> = ({ washer, onClose }) => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule;
  const dispatch = useContext(NotificationDispatch);
  const washersApi = useScrewConnectorWashersApi();

  const defaultValues: WasherFormBody = {
    code: washer?.code ?? null,
    innerDiameter: washer?.innerDiameter ?? null,
    outerDiameter: washer?.outerDiameter ?? null,
    height: washer?.height ?? null,
  };

  const onSubmit = () => async (formData: WasherFormBody) => {
    if (!formData.code || formData.innerDiameter == null || formData.outerDiameter == null || formData.height == null) {
      return;
    }

    try {
      if (washer) {
        const body: UpdateWasherBody = {
          innerDiameter: formData.innerDiameter,
          outerDiameter: formData.outerDiameter,
          height: formData.height,
        };
        await washersApi.updateWasher(washer.code, body);
      } else {
        const body: InsertWasherBody = {
          code: formData.code,
          innerDiameter: formData.innerDiameter,
          outerDiameter: formData.outerDiameter,
          height: formData.height,
        };
        await washersApi.insertWasher(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={washer ? `${i18n.translation.common.edit} ${t.washers.singular}` : `${i18n.translation.common.add} ${t.washers.singular}`}
    >
      <Form<WasherFormBody> submitText={i18n.translation.common.save} onSubmit={onSubmit} defaultValues={defaultValues}>
        {({ control, register, formState: { errors }, reset }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [washer]);

          return (
            <FormFieldsContainer fullWidth>
              <FormField label={t.washers.fields.code} error={errors.code} isOptional={false}>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={!!washer}
                    {...register("code", { required: !isOptional, minLength: 1 })}
                  />
                )}
              </FormField>
              <Controller
                name="innerDiameter"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField label={t.washers.fields.innerDiameter} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={2}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="outerDiameter"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField label={t.washers.fields.outerDiameter} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={2}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="height"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField label={t.washers.fields.height} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={2}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};

export default WasherForm;
